'use client';

import { useEffect, useRef, type CSSProperties } from 'react';
import {
  HUD_SCRAMBLE_CHARS,
  runHudScramble,
} from 'utils/performanceLab/hudScramble';
import { CYCLE_S, PALETTE, PRESET } from './ditherPreset';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './DitherBody.module.css';

/**
 * The figure for "A model is only as good as the nights it has seen".
 *
 * A port of the dither/glitter effect from the editor at
 * ~/Desktop/dither-glitter-editor.html, running its saved preset. The image is
 * sampled onto a grid: cells darker than a threshold are filled, lighter ones
 * are stroked, and the whole grid breathes on a twelve second loop —
 *
 *   expand    the gap opens from 0 to 20, so the step goes 2 to 25 and the
 *             dense dither resolves into a scatter of large cells; colour
 *             drains toward black and white as it goes
 *   fade      the stroked cells drop out and the filled ones shrink to a fifth
 *   hold      four seconds where a share of the remaining cells become plus
 *             signs, each rotating on its own clock, and the biomarkers read
 *   contract  everything reverses and the body comes back
 *
 * The reversal is the point: the figure dissolves into readings and reassembles,
 * which is the section's claim — a body is what the model is trying to see, and
 * these are what it sees it through.
 *
 * The plus signs are the design's own idea and they are why the biomarker
 * labels sit where they do: each one is anchored to a point on the body, so a
 * marker appears to be read off the figure rather than captioned beside it.
 */

/**
 * The four ground-truth labels the model trains against — one per live or
 * announced study.
 *
 * Not ring outputs. An earlier set mixed HRV and RHR in with HbA1c, which is a
 * category error at this point in the page: this fold is about what the pulse
 * is measured *against*, so every chip has to be a label rather than something
 * the ring already produces. HRV next to HbA1c reads as though they are the
 * same kind of thing, and they are opposite ends of the argument.
 *
 * Fixed, not rotating. The standfirst above names three kinds of context —
 * a blood panel, a fitness test, how rested someone felt — and these four
 * instantiate exactly those, with blood pressure carrying the cardiovascular
 * study. Copy and chips are locked together, so cycling them would break the
 * pairing the sentence sets up.
 *
 * WOKE RESTED is the one that matters most and looks least like the others:
 * it is the subjective half of "omics", and it is what keeps the set from
 * reading as a lab printout.
 */
type Marker = { label: string; value: string; at: { x: number; y: number } };

const MARKERS: readonly Marker[] = [
  /* Glucose Metabolism — the three-month glycemic ground truth. */
  { label: 'HbA1c', value: '5.2 %', at: { x: 0.28, y: 0.18 } },
  /* VO2 Max — the same value the app's lab-result screen shows. */
  { label: 'VO₂ max', value: '52.4 mL/kg/min', at: { x: 0.72, y: 0.34 } },
  /* Cardiovascular Load — the most literature-supported waveform target. */
  { label: 'Blood pressure', value: '118/76 mmHg', at: { x: 0.25, y: 0.6 } },
  /* Sleep Sufficiency — the subjective half. */
  { label: 'Woke rested', value: '8/10', at: { x: 0.73, y: 0.79 } },
] as const;

/* Seeded PRNG — the editor's, kept so the same seed gives the same drawing. */
const mulberry32 = (a: number) => () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** The editor's ordered-dither matrix, normalised to 0..1. */
const BAYER = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((r) => r.map((v) => v / 16));

const easeInOut = (t: number) =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

const hexToRgb = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

type Phase = {
  expand: number;
  whiteFade: number;
  plusActive: number;
  plusFadeOut: number;
  plusTime: number;
  darkShrink: number;
};

const REST: Phase = {
  expand: 0,
  whiteFade: 0,
  plusActive: 0,
  plusFadeOut: 0,
  plusTime: 0,
  darkShrink: 0,
};

/** The editor's phase machine, unchanged. */
const phaseAt = (t: number): Phase => {
  const p = PRESET;
  const cycle = t % CYCLE_S;
  const t1 = p.phExpand;
  const t2 = t1 + p.phFade;
  const t3 = t2 + p.phPlus;
  const t4 = t3 + p.phPlusFade;
  const t5 = t4 + p.phContract;

  if (cycle < t1) return { ...REST, expand: easeInOut(cycle / t1) };
  if (cycle < t2) {
    const f = easeInOut((cycle - t1) / p.phFade);
    return { ...REST, expand: 1, whiteFade: f, darkShrink: f };
  }
  if (cycle < t3)
    return {
      expand: 1,
      whiteFade: 1,
      plusActive: 1,
      plusFadeOut: 0,
      plusTime: cycle - t2,
      darkShrink: 1,
    };
  if (cycle < t4)
    return {
      expand: 1,
      whiteFade: 1,
      plusActive: 1,
      plusFadeOut: (cycle - t3) / p.phPlusFade,
      plusTime: cycle - t2,
      darkShrink: 1,
    };
  if (cycle < t5) {
    const c = 1 - easeInOut((cycle - t4) / p.phContract);
    return { ...REST, expand: c, whiteFade: c, darkShrink: c };
  }
  return REST;
};

/** Each plus turns 45 degrees at a time, on its own clock. */
const plusAngle = (phaseTime: number, offset: number) => {
  const rotateDur = 0.15;
  const cycleDur = 0.9 + offset * 0.7;
  const offsetTime = Math.max(0, phaseTime - offset * 1.2);
  const done = Math.floor(offsetTime / cycleDur);
  const pos = offsetTime % cycleDur;
  const base = done * (Math.PI / 4);
  if (pos < rotateDur) return base + easeInOut(pos / rotateDur) * (Math.PI / 4);
  return base + Math.PI / 4;
};

/**
 * The mirror of runHudScramble: instead of resolving glyphs into text left to
 * right, it un-resolves text into glyphs the same way, then blanks.
 *
 * The shared util only goes one direction, and a readout that scrambles in and
 * then merely fades out is lopsided — the arrival is the memorable half and
 * the exit should answer it. Same character pool, so both halves are made of
 * the same noise.
 */
function runHudUnscramble(el: HTMLElement, text: string, durationMs: number) {
  const start = performance.now();
  const step = (now: number) => {
    const q = Math.min((now - start) / durationMs, 1);
    if (q >= 1) {
      el.textContent = '';
      return;
    }
    const kept = Math.ceil((1 - q) * text.length);
    let out = '';
    for (let i = 0; i < text.length; i += 1) {
      if (i < kept) out += text[i];
      else if (text[i] === ' ') out += ' ';
      else
        out +=
          HUD_SCRAMBLE_CHARS[
            Math.floor(Math.random() * HUD_SCRAMBLE_CHARS.length)
          ];
    }
    el.textContent = out;
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

const SRC = getCompressedAssetUrl('/web_v2/pulsomics-1/nights/body.png');

type DitherPalette = Partial<Record<keyof typeof PALETTE, string>>;

/* palette overrides the figure colours (e.g. a light figure for a dark section);
   transparent drops the canvas background so the section behind shows through.
   Both default to the original nights-section look. */
const DitherBody = ({
  palette,
  transparent = false,
  leaders = false,
}: { palette?: DitherPalette; transparent?: boolean; leaders?: boolean } = {}) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const readoutRefs = useRef<(HTMLDivElement | null)[]>([]);
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const valueRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const frame = frameRef.current;
    const canvas = canvasRef.current;
    if (!frame || !canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    /* merged palette — defaults to the module PALETTE when no override is given */
    const pal = { ...PALETTE, ...palette };

    let raf = 0;
    let alive = true;
    let started = 0;
    let pixels: Uint8ClampedArray | null = null;
    let imgW = 0;
    let imgH = 0;
    let wasHolding = false;
    let wasFading = false;
    let exiting = false;
    // Assigned in size() and never read — dead on main too, kept for fidelity.
    let _cssW = 0;
    let _cssH = 0;

    /**
     * The resting frame, pre-rendered.
     *
     * At rest the step is 2, which is seventy thousand cells, and recomputing
     * and redrawing all of them every frame cost 426ms — two frames a second.
     * But at rest almost nothing moves: the grid is static and only the
     * sparkles change. So the static cells are drawn once into an offscreen
     * canvas and blitted, and each frame redraws only the few that glitter.
     *
     * The cache is keyed on the grid shape, so it rebuilds when the container
     * resizes and is simply bypassed once the grid starts expanding — by then
     * the step has grown and the cell count has collapsed to a few hundred,
     * where a full redraw is cheap again.
     */
    let restCanvas: HTMLCanvasElement | null = null;
    let restKey = '';
    let sparkles: {
      cx: number;
      cy: number;
      rw: number;
      r: number;
      g: number;
      b: number;
      gx: number;
      gy: number;
    }[] = [];

    /**
     * Backing store at device pixels, drawing in CSS pixels.
     *
     * The cell is two units and the editor drew it at 1:1, so the grid has to
     * be laid out in CSS pixels to keep that density — but the buffer has to
     * be the display's, or a two-pixel cell is resampled and the whole texture
     * turns to mush. Scaling the context by the ratio gives both: the editor's
     * spacing, at the screen's real resolution.
     *
     * Capped at 2 because the cost is quadratic in the ratio and a 3x buffer
     * buys nothing the eye can find at this cell size.
     */
    let dpr = 1;
    /**
     * The grid is laid out in a fixed unit space, not in CSS pixels.
     *
     * Tying the cell to CSS pixels made the figure's detail depend on how wide
     * the column happened to be — and at a 505px frame a two-pixel cell gives
     * only 250 columns across the body, which is coarse. Fixing the space at
     * 760 units puts 380 columns across it at every size, so the figure holds
     * the same level of detail whatever the layout does, and each cell lands
     * around 2.7 device pixels on a retina screen rather than 4.
     */
    const UNIT_W = 760;
    const UNIT_H = Math.round((UNIT_W * 700) / 640);
    let scale = 1;

    const size = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      restCanvas = null;
      restKey = '';
      const w = frame.clientWidth;
      const h = Math.round((w * 700) / 640);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      _cssW = w;
      _cssH = h;
      scale = (w * dpr) / UNIT_W;
    };

    const draw = (t: number) => {
      if (!pixels) return;
      /* transparent mode clears every frame (the rest-blit path relies on the
         bg fill to wipe the previous frame; without a bg we clear instead) */
      if (transparent) ctx.clearRect(0, 0, canvas.width, canvas.height);
      const p = PRESET;
      const ph = reduce ? REST : phaseAt(t);
      const W = UNIT_W;
      const H = UNIT_H;

      const cell = p.cellSize + ph.expand * p.breatheSize;
      /**
       * A device pixel of overdraw on the dark cells while the grid is tight.
       *
       * At rest the cell and the step are both two units, so the fills should
       * tile — but they land on fractional device pixels, and two antialiased
       * edges meeting never quite add up to one covered pixel. The body came
       * out woven: a mesh of separate squares held apart by a pale lattice,
       * where it should read as one dithered mass. Written against the scale
       * so it stays a pixel at any size, and taken back out as the grid opens,
       * where the gaps are the point.
       */
      const overdraw = ((1 - ph.expand) / scale) * 1.1;
      const gap = p.gap + ph.expand * p.breatheGap;
      const step = cell + gap;
      const cols = Math.floor(W / step);
      const rows = Math.floor(H / step);
      const zoom = 1 + ph.expand * p.breatheZoom;

      /* Rest: blit the cached grid, then draw only the sparkling cells. */
      const atRest = ph.expand === 0;
      const key = `${cols}x${rows}x${canvas.width}`;
      if (atRest && restCanvas && restKey === key) {
        ctx.save();
        /* The cache is a device-pixel buffer being put back at device-pixel
           scale, so any smoothing here is pure loss — it softens a grid whose
           whole character is hard edges. */
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(restCanvas, 0, 0, canvas.width, canvas.height);
        ctx.scale(scale, scale);
        for (let i = 0; i < sparkles.length; i += 1) {
          const sp = sparkles[i];
          const sparkle =
            Math.sin(t * p.glitterSpeed * 3 + sp.gx * 7.3 + sp.gy * 13.7) *
              0.5 +
            0.5;
          const wave =
            Math.sin(t * p.glitterSpeed * 1.5 + sp.gx * 3.1 + sp.gy * 5.9) *
            p.shimmer;
          const a = Math.max(0, Math.min(1, 0.3 + 0.7 * sparkle + wave));
          if (a < 0.01) continue;
          const gr = hexToRgb(pal.glitter);
          ctx.strokeStyle = `rgba(${Math.min(
            255,
            Math.round(sp.r + gr.r * p.glitterBright * sparkle),
          )},${Math.min(
            255,
            Math.round(sp.g + gr.g * p.glitterBright * sparkle),
          )},${Math.min(
            255,
            Math.round(sp.b + gr.b * p.glitterBright * sparkle),
          )},${a})`;
          ctx.lineWidth = p.strokeW;
          ctx.strokeRect(sp.cx - sp.rw / 2, sp.cy - sp.rw / 2, sp.rw, sp.rw);
        }
        ctx.restore();
        return;
      }

      ctx.save();
      ctx.scale(scale, scale);
      if (!transparent) {
        ctx.fillStyle = pal.bg;
        ctx.fillRect(0, 0, W, H);
      }
      ctx.translate(W / 2, H / 2);
      ctx.scale(zoom, zoom);
      ctx.translate(-W / 2, -H / 2);

      const offsetX = (W - cols * step) / 2;
      const offsetY = (H - rows * step) / 2;
      const rng = mulberry32(p.seed);
      const sparkleAcc: typeof sparkles = [];
      const darkRgb = hexToRgb(pal.dark);
      const highRgb = hexToRgb(pal.highlight);
      const midRgb = hexToRgb(pal.mid);
      const glitRgb = hexToRgb(pal.glitter);

      for (let gy = 0; gy < rows; gy += 1) {
        for (let gx = 0; gx < cols; gx += 1) {
          const ix = Math.floor((gx / cols) * imgW);
          const iy = Math.floor((gy / rows) * imgH);
          const ci = (iy * imgW + ix) * 4;
          const a = pixels[ci + 3];
          if (a < 10) {
            rng();
            continue;
          }
          const sr = pixels[ci];
          const sg = pixels[ci + 1];
          const sb = pixels[ci + 2];
          let lum = (0.299 * sr + 0.587 * sg + 0.114 * sb) / 255;
          lum = (lum - 0.5) * p.contrast + 0.5 + p.brightness / 255;
          lum = Math.max(0, Math.min(1, lum));

          let isDark = lum < p.darkThresh;
          let isHigh = lum > p.highThresh;
          if (!isDark && !isHigh) {
            /* Ordered dithering: a mid cell falls to one side or the other
               against its position in the Bayer matrix, which is what breaks
               the midtones into a texture instead of a hard edge. */
            const threshold = BAYER[gy % 4][gx % 4];
            const midLum =
              (lum - p.darkThresh) / (p.highThresh - p.darkThresh + 0.001);
            if (threshold > midLum * p.midProb + (1 - p.midProb) * 0.5)
              isDark = true;
            else isHigh = true;
          }

          const cellRng = rng();
          const isPlus = isDark && cellRng < p.chaos;

          let rw: number;
          if (isDark) {
            const shrink =
              ph.darkShrink > 0 && !(isPlus && ph.plusActive > 0)
                ? 1 - ph.darkShrink * 0.8
                : 1;
            rw = cell * shrink + overdraw;
          } else {
            rw = cell * (p.minRect + (p.maxRect - p.minRect) * lum);
          }

          const base = isDark ? darkRgb : isHigh ? highRgb : midRgb;
          let fr = base.r;
          let fg = base.g;
          let fb = base.b;
          if (!isDark) {
            /* useImageColors: the lit cells keep half the photograph. */
            fr = Math.round((base.r + sr) / 2);
            fg = Math.round((base.g + sg) / 2);
            fb = Math.round((base.b + sb) / 2);
          }
          if (ph.expand > 0) {
            /* Colour drains as the grid opens — but toward the palette's own
               dark and light, not to pure black and white, so the blue holds
               through the whole cycle. */
            const tr = isDark ? darkRgb.r : 255;
            const tg = isDark ? darkRgb.g : 255;
            const tb = isDark ? darkRgb.b : 255;
            fr = Math.round(fr + (tr - fr) * ph.expand);
            fg = Math.round(fg + (tg - fg) * ph.expand);
            fb = Math.round(fb + (tb - fb) * ph.expand);
          }

          let alpha = !isDark && ph.whiteFade > 0 ? 1 - ph.whiteFade : 1;

          const isSparkle = p.glitterOn && isHigh && alpha > 0.1;
          if (isSparkle) {
            if (rng() < p.glitterDensity) {
              if (atRest && restKey !== key) {
                sparkleAcc.push({
                  cx: offsetX + gx * step + cell / 2,
                  cy: offsetY + gy * step + cell / 2,
                  rw: cell * (p.minRect + (p.maxRect - p.minRect) * lum),
                  r: Math.round((highRgb.r + sr) / 2),
                  g: Math.round((highRgb.g + sg) / 2),
                  b: Math.round((highRgb.b + sb) / 2),
                  gx,
                  gy,
                });
              }
              const sparkle =
                Math.sin(t * p.glitterSpeed * 3 + gx * 7.3 + gy * 13.7) * 0.5 +
                0.5;
              const wave =
                Math.sin(t * p.glitterSpeed * 1.5 + gx * 3.1 + gy * 5.9) *
                p.shimmer;
              alpha = Math.max(
                0,
                Math.min(1, (0.3 + 0.7 * sparkle + wave) * (1 - ph.whiteFade)),
              );
              fr = Math.min(
                255,
                Math.round(fr + glitRgb.r * p.glitterBright * sparkle),
              );
              fg = Math.min(
                255,
                Math.round(fg + glitRgb.g * p.glitterBright * sparkle),
              );
              fb = Math.min(
                255,
                Math.round(fb + glitRgb.b * p.glitterBright * sparkle),
              );
            }
          }
          if (alpha < 0.01) continue;

          /* No snapping.
             An earlier pass rounded each cell to the device pixel grid to keep
             the dither crisp, but it rounded in *unit* space — round(v*scale)
             /scale — which quantises the step itself. At the narrow frame's
             scale the 2-unit step became 2.17 and the cells tiled edge to
             edge, turning the dot grid into solid blocks and crossing bars.
             The editor does not snap either; the device-pixel buffer is what
             carries the sharpness. */
          const cx = offsetX + gx * step + cell / 2;
          const cy = offsetY + gy * step + cell / 2;
          const colorStr = `rgba(${fr},${fg},${fb},${alpha})`;

          if (isPlus && ph.plusActive > 0) {
            const dotTime = ph.plusTime - cellRng * 1.5;
            const disappear = (cellRng * 7.3 + 0.17) % 1;
            if (
              dotTime > 0 &&
              (ph.plusFadeOut === 0 || disappear > ph.plusFadeOut)
            ) {
              const ang = plusAngle(dotTime, cellRng);
              const s = cell * 2;
              const half = s / 2;
              const th = s * 0.22;
              ctx.save();
              ctx.translate(cx, cy);
              ctx.rotate(ang);
              ctx.fillStyle = colorStr;
              ctx.fillRect(-half, -th / 2, s, th);
              ctx.fillRect(-th / 2, -half, th, s);
              ctx.restore();
            }
          } else if (isDark) {
            ctx.fillStyle = colorStr;
            ctx.fillRect(cx - rw / 2, cy - rw / 2, rw, rw);
          } else {
            ctx.strokeStyle = colorStr;
            ctx.lineWidth = p.strokeW;
            ctx.strokeRect(cx - rw / 2, cy - rw / 2, rw, rw);
          }
        }
      }
      ctx.restore();

      /* First resting frame at this size: keep the drawing, minus the
         sparkles, so subsequent frames can blit it. */
      if (atRest && restKey !== key) {
        const off = document.createElement('canvas');
        off.width = canvas.width;
        off.height = canvas.height;
        const octx = off.getContext('2d');
        if (octx) {
          octx.drawImage(canvas, 0, 0);
          restCanvas = off;
          restKey = key;
          sparkles = sparkleAcc;
        }
      }

      /**
       * Readouts ride the hold: up once the cells have become plus signs,
       * gone before the body reassembles, so a number is never sitting on a
       * recognisable figure.
       *
       * Each one scrambles in when the hold opens, staggered, and fades on the
       * way out — the same treatment the performance-lab annotations use, so
       * they arrive the way the rest of the site's HUD does rather than simply
       * appearing.
       */
      const holding = ph.plusActive > 0;
      const fading = ph.plusFadeOut > 0;
      /* Up with the hold. Going down is handled per frame below, each one
         following its own value out. */
      const show = holding ? 1 : 0;
      if (holding && !wasHolding) {
        /* Reposition first, then scramble — moving a frame that is already
           resolving reads as a glitch rather than a reading. */
        MARKERS.forEach((m, i) => {
          const el = readoutRefs.current[i];
          if (el) {
            el.style.left = `${m.at.x * 100}%`;
            el.style.top = `${m.at.y * 100}%`;
          }
          const l = labelRefs.current[i];
          if (l) l.textContent = m.label.toUpperCase();
          const v = valueRefs.current[i];
          if (v) {
            v.textContent = '';
            window.setTimeout(() => {
              if (alive && v) runHudScramble(v, m.value, 800);
            }, 120 + i * 190);
          }
        });
      }
      if (fading && !wasFading) {
        /* Out the way it came in: same duration, same stagger, same character
           pool — run at the *start* of the fade window rather than the end, so
           it happens while the frames are still on screen.

           Each frame then clears itself the moment its own value has finished
           un-resolving. They used to wait on one shared opacity, so the last
           reading to go left three empty boxes sitting on the figure behind
           it — the frame outliving the number it was there to hold. */
        exiting = true;
        valueRefs.current.forEach((v, i) => {
          const text = v?.textContent ?? '';
          const el = readoutRefs.current[i];
          const delay = i * 120;
          if (!v || !text) {
            if (el) el.style.opacity = '0';
            return;
          }
          window.setTimeout(() => {
            if (alive && v) runHudUnscramble(v, text, 800);
          }, delay);
          window.setTimeout(() => {
            if (alive && el) el.style.opacity = '0';
          }, delay + 700);
        });
      }
      if (!holding) exiting = false;
      wasHolding = holding;
      wasFading = fading;

      /* While exiting, each frame owns its own opacity — see above. */
      if (!exiting) {
        readoutRefs.current.forEach((el) => {
          if (el) el.style.opacity = String(show);
        });
      }
    };

    const loop = () => {
      if (!alive) return;
      draw((performance.now() - started) / 1000);
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (raf || !pixels) return;
      started = performance.now();
      if (reduce) {
        draw(0);
        return;
      }
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const io = new IntersectionObserver(
      (es) => es.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0.05 },
    );

    const image = new Image();
    // SRC is cross-origin (Speedsize) and the pixels are read back below with
    // getImageData, which taints the canvas without this.
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (!alive) return;
      imgW = image.width;
      imgH = image.height;
      const tmp = document.createElement('canvas');
      tmp.width = imgW;
      tmp.height = imgH;
      const tctx = tmp.getContext('2d', { willReadFrequently: true });
      if (!tctx) return;
      tctx.drawImage(image, 0, 0);
      pixels = tctx.getImageData(0, 0, imgW, imgH).data;
      size();
      io.observe(frame);
    };
    image.src = SRC;

    const onResize = () => {
      size();
      if (pixels) draw((performance.now() - started) / 1000);
    };
    window.addEventListener('resize', onResize);

    return () => {
      alive = false;
      stop();
      io.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [palette, transparent]);

  return (
    <div
      ref={frameRef}
      className={styles.frame}
      style={{
        ...(transparent ? { background: 'transparent' } : {}),
        ...(leaders ? { overflow: 'visible' } : {}),
      }}
      role="img"
      aria-label="A body dissolving into a grid of readings and reassembling"
    >
      <canvas ref={canvasRef} className={styles.surface} />
      {MARKERS.map((m, i) => (
        <div
          key={m.label}
          ref={(el) => {
            readoutRefs.current[i] = el;
          }}
          className={styles.readout}
          data-leaders={leaders ? '' : undefined}
          data-side={m.at.x < 0.5 ? 'left' : 'right'}
          style={
            {
              '--readout-x': `${m.at.x * 100}%`,
              '--readout-y': `${m.at.y * 100}%`,
            } as CSSProperties
          }
          aria-hidden="true"
        >
          <span
            ref={(el) => {
              labelRefs.current[i] = el;
            }}
            className={styles.rLabel}
          >
            {m.label}
          </span>
          <div className={styles.rFrame}>
            <span
              className={`${styles.rDot} ${styles.rDotTop} ${styles.rDotLeft}`}
            />
            <span
              className={`${styles.rDot} ${styles.rDotBottom} ${styles.rDotLeft}`}
            />
            <span
              className={`${styles.rDot} ${styles.rDotTop} ${styles.rDotRight}`}
            />
            <span
              className={`${styles.rDot} ${styles.rDotBottom} ${styles.rDotRight}`}
            />
            <span
              ref={(el) => {
                valueRefs.current[i] = el;
              }}
              className={styles.rValue}
            >
              {m.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DitherBody;
